import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./styles/Chef.css";

const API = "https://okna.co.tz/mrms/Orders.php";

export default function Chef() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const staff = JSON.parse(localStorage.getItem("staff") || "{}");

  const [orders, setOrders] = useState([]);
  const [darkMode, setDarkMode] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  const menuRef = useRef();

  // Redirect if no token
  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
  }, [token, navigate]);

  // Notification permission
  useEffect(() => {
    if ("Notification" in window) {
      Notification.requestPermission();
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 3000);
    return () => clearInterval(interval);
  }, []);

  // Countdown handler - removes orders after 60 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setOrders(prev =>
        prev
          .map(order => {
            if (order.status === "READY" && order.readyAt) {
              const remaining = 60 - Math.floor((Date.now() - order.readyAt) / 1000);
              if (remaining <= 0) {
                // Order expires - remove from view
                return null;
              }
              return { ...order, remaining: Math.max(0, remaining) };
            }
            return order;
          })
          .filter(order => order !== null) // Remove expired orders
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}?chef=true&token=${token}`, {
        method: "GET",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("staff");
        navigate("/");
        return;
      }

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();

      if (!Array.isArray(data)) {
        console.error("Orders API did not return an array:", data);
        return;
      }

      setOrders(prev =>
        data.map(order => {
          const existing = prev.find(o => o.id === order.id);
          if (order.status === "READY") {
            // Only show notification for newly ready orders
            if (!existing?.readyAt) {
              showNotification(order);
            }
            return {
              ...order,
              readyAt: existing?.readyAt || Date.now(),
              remaining: existing?.remaining || 60
            };
          }
          return order;
        })
      );
    } catch (err) {
      console.error("Fetch orders error:", err);
    }
  };

  const showNotification = order => {
    if (Notification.permission === "granted") {
      new Notification("Order Ready 🍽️", {
        body: `#${order.order_number} is READY. Will disappear in 1 minute.`
      });
    }
  };

  const updateStatus = async (id, status) => {
    if (!id) {
      console.error("No order ID provided");
      Swal.fire("Error", "Invalid order ID", "error");
      return;
    }

    const validStatuses = ['PENDING', 'READY', 'PAID'];
    if (!validStatuses.includes(status)) {
      console.error(`Invalid status: ${status}`);
      Swal.fire("Error", `Invalid status. Please use: ${validStatuses.join(', ')}`, "error");
      return;
    }

    setUpdatingOrderId(id);
    
    try {
      const requestBody = { id, status };
      console.log("Sending update request:", requestBody);
      
      const res = await fetch(`${API}?token=${token}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      if (res.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("staff");
        navigate("/");
        return;
      }

      const data = await res.json();
      console.log("Update response:", data);

      if (!res.ok) {
        throw new Error(data.error || `HTTP error! status: ${res.status}`);
      }
      
      if (data.success) {
        // Update local state
        setOrders(prev =>
          prev.map(o =>
            o.id === id
              ? {
                  ...o,
                  status,
                  readyAt: status === "READY" ? Date.now() : null,
                  remaining: status === "READY" ? 60 : null
                }
              : o
          )
        );
        
        // If status is READY, show notification
        if (status === "READY") {
          const order = orders.find(o => o.id === id);
          if (order) {
            showNotification(order);
          }
        }
        
        // Show success message
        Swal.fire({
          title: "✅ Status Updated!",
          text: `Order #${data.id || id} is now ${status}`,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end"
        });
      }
    } catch (err) {
      console.error("Update status error:", err);
      Swal.fire("Error", err.message || "Failed to update order status", "error");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate("/");
  };

  const formatTime = s => {
    if (s < 0) return "0:00";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec < 10 ? "0" : ""}${sec}`;
  };

  return (
    <div className={`chef-page ${darkMode ? "dark" : "light"}`}>
      {/* HEADER */}
      <div className="chef-header">
        <h2>Kitchen Board</h2>

        <div className="right-section">
          <button
            className="mode-toggle"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "☀" : "🌙"}
          </button>

          <div className="user-menu" ref={menuRef}>
            <div
              className="avatar"
              onClick={() => setShowMenu(!showMenu)}
            >
              {staff?.full_name?.charAt(0) || "?"}
            </div>

            {showMenu && (
              <div className="dropdown">
                <p>{staff?.full_name || "User"}</p>
                <button onClick={logout}>Logout</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ORDERS GRID */}
      <div className="orders-grid">
        {orders.length === 0 ? (
          <div className="empty">No active orders</div>
        ) : (
          orders.map(order => (
            <div key={order.id} className={`order-row ${order.status.toLowerCase()}`}>
              <div className="order-top">
                <span className="number">#{order.order_number}</span>
                <span className="waiter">{order.staff_name}</span>
                <span className={`badge ${order.status.toLowerCase()}`}>
                  {order.status}
                </span>
              </div>

              <div className="items">
                {order.items_summary}
              </div>

              {order.status === "READY" && order.remaining !== undefined && (
                <div className="countdown">
                  ⏳ Disappears in: {formatTime(order.remaining)}
                </div>
              )}

              <div className="actions">
                {order.status === "PENDING" && (
                  <button
                    className="prepare-btn"
                    onClick={() => updateStatus(order.id, "READY")}
                    disabled={updatingOrderId === order.id}
                  >
                    {updatingOrderId === order.id ? "Updating..." : "Mark as Ready"}
                  </button>
                )}
                
                {order.status === "READY" && (
                  <button
                    className="completed-btn"
                    disabled
                  >
                    Waiting for Payment
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}